// Path Scope Examples
// --------------------
// Collapsible panel of worked examples for the Denied / Allowed paths
// fields. Path scope rules are subtle (segment match, not substring; deny
// + allow precedence; querystrings ignored) and the inline helper text on
// each field is hitting its readable limit, so the examples stay hidden
// by default behind a "Show examples" toggle.

import React, { useState } from "react";
import { ChevronDown } from "lucide-react";

interface ExampleRow {
  goal: string;
  denied: string;
  allowed: string;
  result: React.ReactNode;
}

const EXAMPLES: ExampleRow[] = [
  {
    goal: "Block all admin pages",
    denied: "admin",
    allowed: "—",
    result: (
      <>
        Blocks <code>/admin</code>, <code>/admin/users</code>,{" "}
        <code>/api/admin/settings</code>. Does <em>not</em> block{" "}
        <code>/administrator</code> (different segment) or{" "}
        <code>/user?role=admin</code> (querystrings aren't matched).
      </>
    ),
  },
  {
    goal: "Block destructive endpoints",
    denied: "delete-account",
    allowed: "—",
    result: (
      <>
        Blocks <code>/api/users/delete-account</code>,{" "}
        <code>/settings/delete-account</code>.
      </>
    ),
  },
  {
    goal: "Test only one page within an area",
    denied: "admin",
    allowed: "admin/dashboard",
    result: (
      <>
        Blocks every admin route <em>except</em>{" "}
        <code>/admin/dashboard</code>.
      </>
    ),
  },
  {
    goal: "Block docs but test the rest of the API",
    denied: "docs",
    allowed: "—",
    result: (
      <>
        Blocks <code>/docs</code>, <code>/api/docs/v2</code>, etc.
      </>
    ),
  },
  {
    goal: "Block multiple unrelated areas",
    denied: "admin, billing",
    allowed: "—",
    result: <>Comma-separated list — both areas excluded.</>,
  },
];

export default function PathScopeExamples() {
  // Hidden by default: most users leave path scope unset (everything in
  // scope is tested), so the worked examples are opt-in detail rather than
  // something to surface up front. Collapsed keeps the step uncluttered;
  // the toggle is right there for anyone configuring denies/exceptions.
  const [open, setOpen] = useState(false);

  return (
    <div className="mb-4">
      <button
        type="button"
        onClick={() => setOpen((previousOpen) => !previousOpen)}
        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
      >
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
        {open ? "Hide examples" : "Show examples"}
      </button>

      {open && (
        <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
          <p className="text-xs text-gray-600 mb-3">
            Denied paths and exceptions match against URL path segments only,
            the parts between the slashes. Querystring values, hostnames,
            and protocols are not considered.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs table-fixed">
              <thead>
                <tr className="border-b border-gray-200 text-left text-[10px] uppercase tracking-wider text-gray-500">
                  <th className="px-2 py-2 font-medium w-1/4">Goal</th>
                  <th className="px-2 py-2 font-medium w-1/6">Denied</th>
                  <th className="px-2 py-2 font-medium w-1/6">Exception</th>
                  <th className="px-2 py-2 font-medium">Result</th>
                </tr>
              </thead>
              <tbody>
                {EXAMPLES.map((example) => (
                  <tr
                    key={example.goal}
                    className="border-b border-gray-100 last:border-b-0 align-top"
                  >
                    <td className="px-2 py-2 text-gray-700 break-words">
                      {example.goal}
                    </td>
                    <td className="px-2 py-2 font-mono text-gray-700 break-words">
                      {example.denied}
                    </td>
                    <td className="px-2 py-2 font-mono text-gray-700 break-words">
                      {example.allowed}
                    </td>
                    <td className="px-2 py-2 text-gray-600 leading-relaxed break-words">
                      {example.result}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
